import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, ArrowRight, BarChart3, CheckCircle2, DollarSign, ShieldCheck, Sparkles, Target, TimerReset } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { getReportDashboardMetrics } from '../utils/reportDashboardMetrics';

const cardShadow = 'shadow-[0_30px_60px_-35px_rgba(28,51,74,0.35)]';
const navy = '#1c334a';
const brand = '#049aaa';
const slate = '#4b5f73';

const money = value => `$${Math.round(Number(value) || 0).toLocaleString()}`;

const SignalCard = ({ icon: Icon, label, value, helper, tone = 'teal' }) => {
  const toneClasses = {
    teal: 'bg-[#049aaa]/10 text-[#049aaa] border-[#c7e6ea]',
    navy: 'bg-[#1c334a]/10 text-[#1c334a] border-[#dbe7e9]',
    amber: 'bg-amber-50 text-amber-700 border-amber-200'
  };

  return (
    <div className="rounded-2xl border border-[#dbe7e9] bg-white/95 p-5">
      <div className="flex items-start gap-4">
        <div className={`rounded-xl border p-3 ${toneClasses[tone] || toneClasses.teal}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#4b5f73]">{label}</div>
          <div className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight text-[#1c334a] tabular-nums">{value}</div>
          {helper && <p className="mt-2 text-sm leading-relaxed text-[#4b5f73]">{helper}</p>}
        </div>
      </div>
    </div>
  );
};

const FindingCard = ({ finding, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.08 * index }}
    className="rounded-2xl border border-[#dbe7e9] bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_45px_rgba(28,51,74,0.10)]"
  >
    <div className="flex items-start gap-3">
      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#049aaa]/10 text-[#049aaa]">
        <CheckCircle2 className="h-4 w-4" />
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-base font-extrabold text-[#1c334a]">{finding.title}</h4>
          <Badge variant="outline" className="border-[#c7e6ea] text-[#049aaa]">{finding.type}</Badge>
        </div>
        <p className="text-sm leading-relaxed text-[#4b5f73]"><strong className="text-[#244358]">Why it matters:</strong> {finding.impact}</p>
        <p className="text-sm leading-relaxed text-[#4b5f73]"><strong className="text-[#244358]">Do this:</strong> {finding.fix}</p>
      </div>
    </div>
  </motion.div>
);

export const PremiumReportDashboard = ({ auditData, report, overallScore }) => {
  const metrics = getReportDashboardMetrics(auditData, report, overallScore);
  const reduceMotion = useReducedMotion();
  const chartData = metrics.sectionScores.map(row => ({ ...row, fill: row.score >= 15 ? brand : row.score >= 10 ? navy : '#9b6829' }));
  const quadrantData = Object.entries(metrics.quadrants).map(([key, row]) => ({ key, ...row }));
  const quickWins = (report?.quick_wins || []).slice(0, 3);
  const valueLabel = metrics.recoverableRange.high > 0 ? metrics.recoverableRange.label : money(metrics.manualWorkCost);

  return (
    <section className="space-y-6" data-testid="premium-diagnostic-dashboard">
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 18 }}
        animate={reduceMotion ? false : { opacity: 1, y: 0 }}
        className={`overflow-hidden rounded-[2rem] border border-[#c7e6ea] bg-white ${cardShadow}`}
      >
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(4,154,170,0.18),transparent_34%),linear-gradient(135deg,#ffffff_0%,#f6fbfc_46%,#eef9f8_100%)] p-6 md:p-9">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#049aaa] via-[#d8e005] to-[#1c334a]" />
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.25fr] lg:items-stretch">
            <div className="rounded-3xl border border-[#dbe7e9] bg-white/85 p-5 md:p-6">
              <Badge className="mb-4 bg-[#1c334a] text-white">#1 first pilot recommendation</Badge>
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#049aaa] text-white shadow-lg">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#049aaa]">Start here</div>
                  <h2 className="mt-2 text-3xl md:text-5xl font-black leading-[0.98] tracking-tight text-[#1c334a]">
                    {metrics.firstPilot.name}
                  </h2>
                </div>
              </div>
              <p className="mt-5 text-lg leading-relaxed text-[#244358]">
                {metrics.firstPilot.why}
              </p>
              <div className="mt-5 rounded-2xl border border-[#c7e6ea] bg-[#f6fbfc] p-4">
                <div className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#4b5f73]">Pilot shape</div>
                <div className="mt-1 text-base font-extrabold text-[#1c334a]">{metrics.firstPilot.shape}</div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="grid gap-4 md:grid-cols-3">
                <SignalCard icon={DollarSign} label="Value to inspect" value={valueLabel} helper="Conservative opportunity range, not promised savings." />
                <SignalCard icon={TimerReset} label="Manual work found" value={money(metrics.manualWorkCost)} helper="Annual cost tied to the work you entered." tone="navy" />
                <SignalCard icon={ShieldCheck} label="Risk posture" value={metrics.riskExposure.level} helper={metrics.riskExposure.label} tone={metrics.riskExposure.level === 'High' ? 'amber' : 'teal'} />
              </div>

              {quickWins.length > 0 && (
                <div className="rounded-3xl border border-[#dbe7e9] bg-white/90 p-5">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#049aaa]">Fastest visible wins</div>
                      <h3 className="text-xl font-black tracking-tight text-[#1c334a]">Top opportunities to compare against the pilot</h3>
                    </div>
                    <Target className="h-5 w-5 text-[#049aaa]" />
                  </div>
                  <div className="grid gap-3">
                    {quickWins.map((win, index) => (
                      <div key={`${win.name}-${index}`} className="flex items-center gap-4 rounded-2xl border border-[#dbe7e9] bg-[#f6fbfc] p-4">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#d8e005] text-sm font-black text-[#1c334a]">{index + 1}</div>
                        <div className="min-w-0 flex-1">
                          <div className="font-extrabold leading-tight text-[#1c334a]">{win.name}</div>
                          <div className="mt-1 text-sm text-[#4b5f73]">Impact {win.impact}/5 · Difficulty {win.difficulty}/5 · <span className="font-bold text-[#049aaa]">{money(win.annual)}/yr</span></div>
                        </div>
                        <ArrowRight className="hidden h-5 w-5 text-[#049aaa] sm:block" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-[1fr_0.85fr]">
        <div className={`rounded-3xl border border-[#dbe7e9] bg-white p-5 ${cardShadow}`}>
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-black tracking-tight text-[#1c334a]">Where the pressure is coming from</h3>
              <p className="text-sm text-[#4b5f73]">This keeps the diagnosis scannable without making the score the main story.</p>
            </div>
            <BarChart3 className="h-5 w-5 text-[#049aaa]" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 30 }}>
                <CartesianGrid stroke="#dbe7e9" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: slate, fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={55} />
                <YAxis tick={{ fill: slate, fontSize: 11 }} />
                <Tooltip formatter={(value) => [`${value}`, 'Score']} />
                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                  {chartData.map(entry => <Cell key={entry.key} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`rounded-3xl border border-[#dbe7e9] bg-white p-5 ${cardShadow}`}>
          <h3 className="text-2xl font-black tracking-tight text-[#1c334a]">Opportunity shape</h3>
          <p className="mt-1 text-sm text-[#4b5f73]">Use this to avoid chasing the wrong work first.</p>
          <div className="mt-5 space-y-4">
            {quadrantData.map(row => {
              const percent = metrics.manualWorkCost > 0 ? Math.round((row.value / metrics.manualWorkCost) * 100) : 0;
              return (
                <div key={row.key}>
                  <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                    <span className="font-extrabold text-[#244358]">{row.label}</span>
                    <span className="text-[#4b5f73]">{row.count} · {money(row.value)}</span>
                  </div>
                  <Progress value={percent} className="h-2" />
                </div>
              );
            })}
          </div>
          <div className="mt-5 rounded-2xl border border-[#dbe7e9] bg-[#f6fbfc] p-4 text-sm leading-relaxed text-[#4b5f73]">
            Best current leverage area: <strong className="text-[#1c334a]">{metrics.topCategory}</strong>. Keep the first pilot narrow enough to prove in two weeks.
          </div>
        </div>
      </div>

      <div className={`rounded-3xl border border-[#dbe7e9] bg-white p-5 ${cardShadow}`}>
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-black tracking-tight text-[#1c334a]">Guardrails that make the pilot safe</h3>
            <p className="text-sm text-[#4b5f73]">These are the controls to put in place before the business scales AI usage.</p>
          </div>
          <Badge variant="outline" className="border-[#9ccfd4] text-[#049aaa]">Evidence-backed</Badge>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {metrics.findings.map((finding, index) => <FindingCard key={`${finding.title}-${index}`} finding={finding} index={index} />)}
        </div>
        {metrics.riskExposure.level === 'High' && (
          <div className="mt-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p><strong>Do not skip this:</strong> use the pilot for preparation and decision support first. Keep final customer, financial, and commitment decisions human-approved.</p>
          </div>
        )}
      </div>
    </section>
  );
};
